// src/pages/admin/PaymentManagement.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Info, AlertCircle } from 'lucide-react';
import Badge from '../../components/ui/Badge';
import Alert from '../../components/ui/Alert';
import SearchBar from '../../components/common/SearchBar';
import FilterDropdown from '../../components/common/FilterDropdown';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

const PaymentManagement = () => {

  const {
    canViewPayments,
    canApprovePayments,
    canRefundPayments,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({ 
    totalRevenue: 0, 
    monthlyRevenue: 0,
    pendingPayments: 0,
    approvedPayments: 0,
    rejectedPayments: 0
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [formData, setFormData] = useState({ status: '', note: '' });

  useEffect(() => {
    if (canViewPayments) {
      fetchPayments();
      fetchStats();
    }
  }, [filters, canViewPayments]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const response = await axios.get(`${API_URL}/admin/payments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPayments(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError(err.response?.data?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('medigo_token');
      const response = await axios.get(`${API_URL}/admin/payments/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to load payment stats');
    }
  };

 
  const handleUpdateStatus = async (status) => {
    if (!canApprovePayments) {
      setError('You do not have permission to approve/reject payments');
      return;
    }

    try {
      const token = localStorage.getItem('medigo_token');
      await axios.patch(
        `${API_URL}/admin/payments/${selectedPayment._id}/status`,
        { status: status, verificationNotes: formData.note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Payment ${status} successfully`);
      setShowModal(false);
      setFormData({ status: '', note: '' });
      fetchPayments();
      fetchStats();
    } catch (err) {
      console.error('Error updating payment:', err);
      setError(err.response?.data?.message || 'Failed to update payment');
    }
  };

  
  const handleRefund = async () => {
    if (!canRefundPayments) {
      setError('Only Super Admins can process refunds');
      setShowModal(false);
      return;
    }

    try {
      const token = localStorage.getItem('medigo_token');
      await axios.post(
        `${API_URL}/admin/payments/${selectedPayment._id}/refund`,
        { reason: formData.note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Payment refunded successfully');
      setShowModal(false);
      setFormData({ status: '', note: '' });
      fetchPayments();
      fetchStats();
    } catch (err) {
      console.error('Error refunding payment:', err);
      setError(err.response?.data?.message || 'Failed to refund payment');
    }
  };

  const openModal = (payment, type) => {
   
    if (type === 'refund' && !canRefundPayments) {
      setError('Only Super Admins can process refunds');
      return;
    }
    if ((type === 'approve' || type === 'reject') && !canApprovePayments) {
      setError('You do not have permission to approve/reject payments');
      return;
    }

    setSelectedPayment(payment);
    setActionType(type);
    setFormData({ status: '', note: '' });
    setShowModal(true);
  };

 
  if (!canViewPayments) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          Payment Management
          {isSuperAdmin && <Shield className="w-7 h-7 text-purple-600" />}
        </h1>
        <p className="text-gray-600 mt-1">Monitor and verify hospital subscription payments</p>
      </div>

      {/* Permission Badge */}
      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              You can approve payments and process refunds
            </p>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can approve and reject payments. Only Super Admin can process refunds.
            </p>
          </div>
        </div>
      ) : null}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <p className="text-green-100 text-sm">Total Revenue</p>
          <p className="text-3xl font-bold mt-2">KES {stats.totalRevenue?.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <p className="text-blue-100 text-sm">Monthly Revenue</p>
          <p className="text-3xl font-bold mt-2">KES {stats.monthlyRevenue?.toLocaleString()}</p>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
          <p className="text-yellow-100 text-sm">Pending</p>
          <p className="text-3xl font-bold mt-2">{stats.pendingPayments || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <p className="text-purple-100 text-sm">Approved</p>
          <p className="text-3xl font-bold mt-2">{stats.approvedPayments || 0}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SearchBar
            placeholder="Search by name, phone, email, or transaction code..."
            onSearch={(value) => setFilters({ ...filters, search: value, page: 1 })}
            value={filters.search}
          />
          <FilterDropdown
            label="Status"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
            options={[
              { label: 'All Status', value: '' },
              { label: 'Pending', value: 'pending' },
              { label: 'Approved', value: 'approved' },
              { label: 'Rejected', value: 'rejected' },
            ]}
          />
          <button
            onClick={fetchPayments}
            className="bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>No payments found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">User Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Hospital</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">M-PESA Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{payment.user?.name || 'N/A'}</div>
                        <div className="text-gray-600">{payment.user?.email || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{payment.hospital?.name || 'N/A'}</div>
                        <div className="text-gray-600 text-xs">{payment.hospital?.email || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm">
                        <div className="font-mono text-gray-900">
                           {payment.mpesaDetails?.phoneNumber || 'N/A'}
                        </div>
                        <div className="font-mono text-blue-600 font-semibold">
                           {payment.mpesaDetails?.transactionCode || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge variant="info">
                        {payment.plan?.toUpperCase() || 'FREE'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm font-semibold text-gray-900">
                        KES {payment.amount?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <Badge variant={
                        payment.status === 'approved' ? 'success' :
                        payment.status === 'pending' ? 'warning' : 'error'
                      }>
                        {payment.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {new Date(payment.submittedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {/* Approve/Reject - Both admins can do this */}
                        {payment.status === 'pending' && canApprovePayments && (
                          <>
                            <button
                              onClick={() => openModal(payment, 'approve')}
                              className="text-green-600 hover:text-green-700 text-sm font-medium"
                              title="Approve Payment"
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => openModal(payment, 'reject')}
                              className="text-red-600 hover:text-red-700 text-sm font-medium"
                              title="Reject Payment"
                            >
                              ✗ Reject
                            </button>
                          </>
                        )}
                        
                        {/* Refund - ONLY Super Admin */}
                        {payment.status === 'approved' && (
                          canRefundPayments ? (
                            <button
                              onClick={() => openModal(payment, 'refund')}
                              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                              title="Refund Payment (Super Admin Only)"
                            >
                              ↩ Refund
                            </button>
                          ) : (
                            <span
                              className="text-gray-400 text-sm cursor-not-allowed"
                              title="Only Super Admins can process refunds"
                            >
                              ↩ Refund
                            </span>
                          )
                        )}
                        
                        {payment.status === 'rejected' && (
                          <span className="text-gray-500 text-sm">No actions</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === pagination.pages}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">
              {actionType === 'approve' && '✓ Approve Payment'}
              {actionType === 'reject' && '✗ Reject Payment'}
              {actionType === 'refund' && '↩ Refund Payment'}
            </h3>
            
            {selectedPayment && (
              <div className="mb-4 p-4 bg-gray-50 rounded">
                <p className="text-sm mb-1"><strong>User:</strong> {selectedPayment.user?.name}</p>
                <p className="text-sm mb-1"><strong>Email:</strong> {selectedPayment.user?.email}</p>
                <p className="text-sm mb-1"><strong>Phone:</strong> {selectedPayment.mpesaDetails?.phoneNumber}</p>
                <p className="text-sm mb-1"><strong>Transaction:</strong> {selectedPayment.mpesaDetails?.transactionCode}</p>
                <p className="text-sm mb-1"><strong>Plan:</strong> {selectedPayment.plan?.toUpperCase()}</p>
                <p className="text-sm"><strong>Amount:</strong> KES {selectedPayment.amount?.toLocaleString()}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {actionType === 'refund' ? 'Refund Reason *' : 'Verification Note *'}
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder={actionType === 'refund' ? 'Enter reason for refund...' : 'Enter verification note...'}
                  required
                />
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => {
                    if (!formData.note.trim()) {
                      setError('Please provide a note/reason');
                      return;
                    }
                    if (actionType === 'approve') {
                      handleUpdateStatus('approved');
                    } else if (actionType === 'reject') {
                      handleUpdateStatus('rejected');
                    } else {
                      handleRefund();
                    }
                  }}
                  disabled={!formData.note}
                  className={`flex-1 py-2 rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                    actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                    actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-orange-600 hover:bg-orange-700'
                  } text-white`}
                >
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setFormData({ status: '', note: '' });
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;